/**
 * Instalarea dashboard-ului ca aplicație (PWA / „Add to Home Screen").
 *
 * Chromium (Android/desktop) anunță instalabilitatea prin `beforeinstallprompt`,
 * un eveniment care poate sosi ORICÂND după load - de-aceea îl capturăm la nivel
 * de modul (initPwaInstall, chemat la mount în main.tsx), nu în componenta
 * Settings, care poate nici să nu fie montată atunci. iOS/Safari n-are echivalent
 * programatic: acolo UI-ul arată pașii manuali (Share → Add to Home Screen).
 */

// Subsetul de care avem nevoie din evenimentul Chromium (nu există în lib.dom).
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const subs = new Set<() => void>();

/** Capturează promptul de instalare; o singură dată, la pornirea aplicației. */
export function initPwaInstall(): void {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // altfel Chrome arată singur mini-infobar-ul, în locul butonului nostru
    deferredPrompt = e as BeforeInstallPromptEvent;
    subs.forEach((f) => f());
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    subs.forEach((f) => f());
  });
}

/** Anunță componenta (Settings) când starea de instalare se schimbă. */
export function subscribeInstall(f: () => void): () => void {
  subs.add(f);
  return () => subs.delete(f);
}

/** Deschide dialogul nativ de instalare (doar când starea e "prompt"). */
export async function promptInstall(): Promise<void> {
  const p = deferredPrompt;
  if (!p) return;
  deferredPrompt = null; // promptul Chromium e de unică folosință
  subs.forEach((f) => f());
  await p.prompt();
}

/**
 * iPad-urile recente se prezintă ca „Macintosh" în UA; le deosebim de un Mac
 * real prin touch (un Mac n-are maxTouchPoints > 1). Pură, ca să fie testabilă.
 */
export function isIos(ua: string, maxTouchPoints: number): boolean {
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && maxTouchPoints > 1;
}

export type InstallState = "installed" | "prompt" | "ios" | "none";

/** Starea curentă, în ordinea priorității: deja instalat > buton > ghid iOS > nimic. */
export function installState(): InstallState {
  // `navigator.standalone` e proprietatea istorică Safari; display-mode acoperă restul.
  if (matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true) {
    return "installed";
  }
  if (deferredPrompt) return "prompt";
  if (isIos(navigator.userAgent, navigator.maxTouchPoints)) return "ios";
  return "none";
}
